/**
 * DailyLogs — staff daily-log editor with allergy-safety enforcement.
 *
 * Workflow:
 *  • Pick a child (defaults to first child).
 *  • Add meals, water, naps, toileting, mood, activities, notes.
 *  • Tap "Generate today's story" to compose the parent-facing AI story.
 *  • If a meal includes an allergen on the child's Admissions profile,
 *    the backend returns 409 and we surface a calm, impossible-to-miss
 *    red banner without ever logging the unsafe entry.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Sparkles, AlertTriangle } from "lucide-react";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { AdminHeader } from "./_shared";

function Section({ title, addCta, items, renderItem, onAdd, testid }) {
  return (
    <section
      data-testid={testid}
      className="rounded-2xl bg-white border border-[#EEF0F4] p-5 space-y-3"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-display text-base text-[#1A1B2E]">{title}</h3>
        <button
          onClick={onAdd}
          data-testid={`${testid}-add`}
          className="inline-flex items-center gap-1 text-xs font-medium text-[#1F3A2A] hover:underline"
        >
          <Plus size={12} strokeWidth={1.8} />
          {addCta}
        </button>
      </div>
      {items.length === 0 ? (
        <p className="text-xs italic text-[#9CA3AF]">No entries yet.</p>
      ) : (
        <ul className="space-y-2">{items.map(renderItem)}</ul>
      )}
    </section>
  );
}

export default function DailyLogs() {
  const { t } = useI18n();
  const [children, setChildren] = useState([]);
  const [childId, setChildId] = useState("");
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [story, setStory] = useState({ generating: false });
  const [modal, setModal] = useState(null); // {kind, form}

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/children");
        setChildren(r.data || []);
        if (r.data?.length) setChildId(r.data[0].id);
      } catch {
        // ignore
      }
    })();
  }, []);

  useEffect(() => {
    if (!childId) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/admin/ops/daily-logs/${childId}`);
        setLog(r.data);
      } catch {
        // ignore
      }
      setLoading(false);
    })();
  }, [childId]);

  const totalNapMin = useMemo(
    () => (log?.naps || []).reduce((s, n) => s + (n.duration_minutes || 0), 0),
    [log]
  );
  const totalWater = useMemo(
    () => (log?.water_intake || []).reduce((s, w) => s + (w.ounces || 0), 0),
    [log]
  );

  const post = async (path, body, successMsg) => {
    try {
      const r = await api.post(`/admin/ops/daily-logs/${childId}/${path}`, body);
      setLog(r.data);
      if (successMsg) toast.success(successMsg);
      setModal(null);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.kind === "allergy_match") {
        toast.error(
          `⚠ ${t("ops.daily_logs.allergy_warning_blocked")} (${detail.matched_allergens.join(", ")})`,
          { duration: 8000 }
        );
        setModal(null);
        return;
      }
      toast.error(detail || t("ops.errors.generic"));
    }
  };

  const generateStory = async () => {
    setStory({ generating: true });
    try {
      const r = await api.post(`/admin/ops/daily-logs/${childId}/generate-story`);
      setLog(r.data);
      toast.success("Today's story is ready.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ops.errors.generic"));
    }
    setStory({ generating: false });
  };

  return (
    <main className="p-6 lg:p-10 space-y-6" data-testid="admin-daily-logs">
      <AdminHeader
        eyebrow={t("ops.daily_logs.eyebrow")}
        title={t("ops.daily_logs.title")}
        action={
          <select
            value={childId}
            onChange={(e) => setChildId(e.target.value)}
            data-testid="daily-logs-child-picker"
            className="bg-white border border-[#EEF0F4] rounded-full px-4 py-2 text-sm focus:outline-none focus:border-[#1F3A2A]"
          >
            {children.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        }
      />

      {loading || !log ? (
        <p className="font-serif italic text-[#525252]">{t("ops.common.loading")}</p>
      ) : (
        <>
          {/* Today summary */}
          <section className="rounded-3xl bg-[#FAF9F1] border border-[#EEF0F4] p-5 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="daily-logs-summary">
            <div><div className="eyebrow">{t("ops.daily_logs.summary_meals")}</div><div className="mt-1 font-display text-2xl text-[#1A1B2E] tabular-nums">{log.meals.length}</div></div>
            <div><div className="eyebrow">{t("ops.daily_logs.summary_rest")}</div><div className="mt-1 font-display text-2xl text-[#1A1B2E] tabular-nums">{Math.floor(totalNapMin / 60)}h {totalNapMin % 60}m</div></div>
            <div><div className="eyebrow">{t("ops.daily_logs.summary_water")}</div><div className="mt-1 font-display text-2xl text-[#1A1B2E] tabular-nums">{totalWater} oz</div></div>
            <div><div className="eyebrow">{t("ops.daily_logs.summary_mood")}</div><div className="mt-1 font-display text-2xl text-[#1A1B2E]">{log.mood_entries.length}</div></div>
          </section>

          <div className="grid lg:grid-cols-2 gap-4">
            <Section
              title={t("ops.daily_logs.section_meals")}
              addCta={t("ops.daily_logs.add_meal")}
              items={log.meals}
              onAdd={() => setModal({ kind: "meal", form: { meal_type: "lunch", food_items: "", amount: "most", notes: "" } })}
              testid="daily-logs-meals"
              renderItem={(m) => (
                <li key={m.id} className="rounded-xl bg-[#FAF9F1]/40 border border-[#EEF0F4] p-3 text-sm">
                  <div className="font-medium text-[#1A1B2E]">{t(`ops.meal_type.${m.meal_type}`)}</div>
                  <div className="text-xs text-[#6B7280] mt-0.5">{m.food_items.join(", ")}</div>
                  <div className="text-xs mt-1">{t(`ops.amount.${m.amount}`)}</div>
                </li>
              )}
            />
            <Section
              title={t("ops.daily_logs.section_naps")}
              addCta={t("ops.daily_logs.add_nap")}
              items={log.naps}
              onAdd={() => setModal({ kind: "nap", form: { started_at: new Date().toISOString().slice(0, 16), duration_minutes: 60, quality: "restful", notes: "" } })}
              testid="daily-logs-naps"
              renderItem={(n) => (
                <li key={n.id} className="rounded-xl bg-[#FAF9F1]/40 border border-[#EEF0F4] p-3 text-sm">
                  <div className="font-medium text-[#1A1B2E]">{n.duration_minutes || 0} min · {t(`ops.nap_quality.${n.quality}`)}</div>
                  <div className="text-xs text-[#6B7280] mt-0.5">started {n.started_at?.slice(11, 16)}</div>
                </li>
              )}
            />
            <Section
              title={t("ops.daily_logs.section_water")}
              addCta={t("ops.daily_logs.add_water")}
              items={log.water_intake}
              onAdd={() => setModal({ kind: "water", form: { ounces: 4 } })}
              testid="daily-logs-water"
              renderItem={(w) => (
                <li key={w.id} className="rounded-xl bg-[#FAF9F1]/40 border border-[#EEF0F4] p-3 text-sm">
                  <span className="text-[#1A1B2E]">{w.ounces} oz</span>
                  <span className="text-xs text-[#6B7280] ml-2">{w.recorded_at?.slice(11, 16)}</span>
                </li>
              )}
            />
            <Section
              title={t("ops.daily_logs.section_mood")}
              addCta={t("ops.daily_logs.add_mood")}
              items={log.mood_entries}
              onAdd={() => setModal({ kind: "mood", form: { mood: "happy", notes: "" } })}
              testid="daily-logs-mood"
              renderItem={(m) => (
                <li key={m.id} className="rounded-xl bg-[#FAF9F1]/40 border border-[#EEF0F4] p-3 text-sm">
                  <span className="text-[#1A1B2E]">{t(`ops.mood.${m.mood}`)}</span>
                  <span className="text-xs text-[#6B7280] ml-2">{m.recorded_at?.slice(11, 16)}</span>
                </li>
              )}
            />
            <Section
              title={t("ops.daily_logs.section_toileting")}
              addCta={t("ops.daily_logs.add_toileting")}
              items={log.toileting}
              onAdd={() => setModal({ kind: "toileting", form: { kind: "diaper_wet", notes: "" } })}
              testid="daily-logs-toileting"
              renderItem={(t1) => (
                <li key={t1.id} className="rounded-xl bg-[#FAF9F1]/40 border border-[#EEF0F4] p-3 text-sm">
                  <span className="text-[#1A1B2E]">{t(`ops.toileting.${t1.kind}`)}</span>
                  <span className="text-xs text-[#6B7280] ml-2">{t1.recorded_at?.slice(11, 16)}</span>
                </li>
              )}
            />
            <Section
              title={t("ops.daily_logs.section_activities")}
              addCta={t("ops.daily_logs.add_activity")}
              items={log.activities}
              onAdd={() => setModal({ kind: "activity", form: { name: "", category: "outdoor", duration_minutes: 30, notes: "" } })}
              testid="daily-logs-activities"
              renderItem={(a) => (
                <li key={a.id} className="rounded-xl bg-[#FAF9F1]/40 border border-[#EEF0F4] p-3 text-sm">
                  <div className="font-medium text-[#1A1B2E]">{a.name}</div>
                  <div className="text-xs text-[#6B7280] mt-0.5">{t(`ops.activity_category.${a.category}`)} · {a.duration_minutes}m</div>
                </li>
              )}
            />
          </div>

          {/* Parent story card */}
          <section
            data-testid="daily-logs-story-card"
            className="rounded-3xl bg-[#1F3A2A] text-white p-6 lg:p-8"
          >
            <div className="flex items-start gap-3">
              <Sparkles size={20} strokeWidth={1.4} className="opacity-80" />
              <div className="flex-1">
                <div className="eyebrow text-white/80">{t("ops.daily_logs.section_story")}</div>
                {log.parent_story ? (
                  <p className="mt-2 font-serif text-lg leading-relaxed" data-testid="daily-logs-story-text">
                    {log.parent_story}
                  </p>
                ) : (
                  <p className="mt-2 font-serif italic text-white/80">
                    {t("ops.daily_logs.story_not_generated_yet")}
                  </p>
                )}
                <button
                  onClick={generateStory}
                  disabled={story.generating}
                  data-testid="daily-logs-generate-story"
                  className={`mt-4 inline-flex items-center gap-2 rounded-full bg-white text-[#1F3A2A] text-sm font-medium px-4 py-2 hover:bg-white/90 ${story.generating ? "opacity-60" : ""}`}
                >
                  {log.parent_story ? t("ops.daily_logs.regenerate_story") : t("ops.daily_logs.generate_story")}
                </button>
              </div>
            </div>
          </section>
        </>
      )}

      {modal && (
        <Modal
          modal={modal}
          setModal={setModal}
          onSubmit={(body, path, msg) => post(path, body, msg)}
          t={t}
        />
      )}
    </main>
  );
}

function Modal({ modal, setModal, onSubmit, t }) {
  const [form, setForm] = useState(modal.form);
  const submit = () => {
    const path = modal.kind === "mood" ? "mood" : modal.kind === "toileting" ? "toileting" : modal.kind === "water" ? "water" : modal.kind === "nap" ? "nap" : modal.kind === "activity" ? "activity" : "meal";
    const body =
      modal.kind === "meal"
        ? { ...form, food_items: form.food_items.split(",").map((s) => s.trim()).filter(Boolean) }
        : modal.kind === "nap"
        ? { ...form, started_at: new Date(form.started_at).toISOString(), duration_minutes: Number(form.duration_minutes), ended_at: null }
        : modal.kind === "water"
        ? { ounces: Number(form.ounces) }
        : modal.kind === "activity"
        ? { ...form, duration_minutes: Number(form.duration_minutes) }
        : form;
    onSubmit(body, path);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid={`daily-logs-modal-${modal.kind}`}
      onClick={() => setModal(null)}
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
    >
      <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-xl text-[#1A1B2E] capitalize">{t(`ops.daily_logs.add_${modal.kind}`)}</h3>
        <div className="mt-5 space-y-3">
          {modal.kind === "meal" && (
            <>
              <select value={form.meal_type} onChange={(e) => setForm({ ...form, meal_type: e.target.value })} data-testid="meal-form-type" className="w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm bg-white">
                {["breakfast", "snack_morning", "lunch", "snack_afternoon"].map((k) => (<option key={k} value={k}>{t(`ops.meal_type.${k}`)}</option>))}
              </select>
              <input type="text" placeholder={t("ops.daily_logs.food_items_label")} value={form.food_items} onChange={(e) => setForm({ ...form, food_items: e.target.value })} data-testid="meal-form-foods" className="w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm" />
              <select value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="meal-form-amount" className="w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm bg-white">
                {["all", "most", "some", "little", "none"].map((k) => (<option key={k} value={k}>{t(`ops.amount.${k}`)}</option>))}
              </select>
            </>
          )}
          {modal.kind === "nap" && (
            <>
              <input type="datetime-local" value={form.started_at} onChange={(e) => setForm({ ...form, started_at: e.target.value })} data-testid="nap-form-started" className="w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm" />
              <input type="number" placeholder={t("ops.daily_logs.duration_label")} value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} data-testid="nap-form-duration" className="w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm" />
              <select value={form.quality} onChange={(e) => setForm({ ...form, quality: e.target.value })} className="w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm bg-white">
                {["restful", "restless", "skipped"].map((k) => (<option key={k} value={k}>{t(`ops.nap_quality.${k}`)}</option>))}
              </select>
            </>
          )}
          {modal.kind === "water" && (
            <input type="number" placeholder={t("ops.daily_logs.ounces_label")} value={form.ounces} onChange={(e) => setForm({ ...form, ounces: e.target.value })} data-testid="water-form-oz" className="w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm" />
          )}
          {modal.kind === "mood" && (
            <select value={form.mood} onChange={(e) => setForm({ ...form, mood: e.target.value })} data-testid="mood-form-mood" className="w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm bg-white">
              {["happy", "calm", "tired", "playful", "quiet", "fussy", "unsettled"].map((m) => (<option key={m} value={m}>{t(`ops.mood.${m}`)}</option>))}
            </select>
          )}
          {modal.kind === "toileting" && (
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="toileting-form-kind" className="w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm bg-white">
              {["diaper_wet", "diaper_bm", "diaper_dry", "toilet_pee", "toilet_bm", "accident"].map((k) => (<option key={k} value={k}>{t(`ops.toileting.${k}`)}</option>))}
            </select>
          )}
          {modal.kind === "activity" && (
            <>
              <input type="text" placeholder="Activity name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="activity-form-name" className="w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm" />
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="activity-form-category" className="w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm bg-white">
                {["outdoor", "art", "music", "story", "sensory", "social", "physical", "rest", "other"].map((c) => (<option key={c} value={c}>{t(`ops.activity_category.${c}`)}</option>))}
              </select>
              <input type="number" placeholder={t("ops.daily_logs.duration_label")} value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm" />
            </>
          )}
        </div>
        <div className="mt-6 flex items-center justify-end gap-3">
          <button onClick={() => setModal(null)} className="text-sm text-[#6B7280] hover:text-[#1A1B2E]">{t("ops.common.cancel")}</button>
          <button onClick={submit} data-testid={`daily-logs-modal-submit`} className="inline-flex items-center gap-2 rounded-full bg-[#1F3A2A] text-white text-sm font-medium px-4 py-2 hover:bg-[#132C17]">{t("ops.daily_logs.submit")}</button>
        </div>
      </div>
    </div>
  );
}
